import React from "react";
import classNames from "classnames";
import {
  GetServerSideProps,
  GetServerSidePropsContext,
  GetServerSidePropsResult,
  NextPage,
} from "next";
import Link from "next/link";

import getPages from "api/requests/wordpress/getPages";
import getTerms from "api/requests/wordpress/getTerms";
import { MenuItems, Page, Term } from "custom-types/Wordpress";
import { getCountryCodeHeader } from "utils/requestContext";

import Breadcrumb from "components/Breadcrumb";
import Breadcrumbs from "components/Breadcrumbs";
import Image from "components/Image";
import NewsAdAdditional from "components/news/Ads/NewsAdAdditional";
import NewsAdBillboard from "components/news/Ads/NewsAdBillboard";
import NewsAdLower from "components/news/Ads/NewsAdLower";
import NewsAdUpper from "components/news/Ads/NewsAdUpper";
import GlossaryHead from "components/news/GlossaryHead";
import GlossaryNavigation from "components/news/GlossaryNavigation";
import NewsRequiredContainer from "components/news/NewsRequiredContainer";
import NewsSideBar from "components/news/NewsSideBar";
import NewsSocialShareBar from "components/news/NewsSocialShareBar";

type NewsProps = {
  menuItems: MenuItems;
  page: Page;
  terms: Term[];
  currentLetter: string;
};

const CannabisGlossaryPage: NextPage<NewsProps> = ({
  terms,
  menuItems,
  page,
  currentLetter,
}) => {
  const {
    disableAds = false,
    styles = [],
    scripts = [],
    schemaJson = "",
    id,
    regionRestriction = "WW",
    seoHeadContent = "",
    title = "",
    link,
  } = page;

  let additionalAds = 0;

  return (
    <NewsRequiredContainer
      styles={styles}
      scripts={scripts}
      id={id}
      regionRestriction={regionRestriction}
      seoHeadContent={seoHeadContent}
      title={title}
      schemaJson={schemaJson}
      currentLetter={currentLetter}
      slug="cannabis-glossary"
    >
      <GlossaryHead currentLetter={currentLetter} />
      <Breadcrumbs className="mt-lg">
        <Breadcrumb href="/learn">Leafly Learn</Breadcrumb>
        <Breadcrumb href="/learn/cannabis-glossary">
          Cannabis Glossary
        </Breadcrumb>
        <Breadcrumb
          href={`/learn/cannabis-glossary?letter=${currentLetter}`}
          currentLocation={true}
        >
          {currentLetter.toUpperCase()}
        </Breadcrumb>
      </Breadcrumbs>
      {!disableAds && <NewsAdBillboard />}
      <div className="container mb-section mt-sm relative">
        <div className="md:flex items-center justify-between mb-lg md:mb-none">
          <h1 className="mb-sm md:mb-none">Cannabis Glossary</h1>
          <NewsSocialShareBar link={link} title={title} />
        </div>
        <GlossaryNavigation currentLetter={currentLetter} />
        <div className="row">
          <div className="col md:col-3">
            <NewsSideBar
              menuItems={menuItems}
              disableAds={disableAds}
              optionLabel="Select a term"
            />
          </div>
          <div className="col md:col-9">
            {terms.map((term, i) => {
              const excerpt = `${term.excerpt.slice(
                0,
                term.excerpt.lastIndexOf(".") + 1,
              )}</p>`;

              const insertAdditionalAd =
                i !== 0 && i % 3 === 0 && i < terms.length - 3;
              if (insertAdditionalAd) {
                additionalAds++;
              }

              return (
                <React.Fragment key={`glossary-term-${term.id}`}>
                  <Link
                    href={{
                      pathname: "/learn/cannabis-glossary/[termSlug]",
                      query: { termSlug: term.slug },
                    }}
                    key={term.id}
                    className="flex row mb-xxl relative w-full"
                  >
                    {term.featuredMedia.url && (
                      <div className="col md:col-4 mb-sm md:mb-none">
                        <Image
                          src={term.featuredMedia.url}
                          alt={term.featuredMedia.altText}
                          sizes={[544, 768, 175, 240, null, 240]}
                          ratio="rectangle"
                        />
                      </div>
                    )}
                    <div
                      className={classNames("col", {
                        "md:col-8": !!term.featuredMedia.url,
                      })}
                    >
                      <div className="font-bold text-md">{term.title}</div>
                      <div
                        className="block mt-sm mb-none text-sm"
                        dangerouslySetInnerHTML={{ __html: excerpt }}
                      />
                      <span className="text-green underline text-xs block -mt-sm">
                        Read more
                      </span>
                    </div>
                  </Link>
                  {(i === 0 || insertAdditionalAd) && !disableAds && (
                    <div className="my-xxl">
                      {i === 0 ? <NewsAdUpper /> : <></>}
                      {insertAdditionalAd && (
                        <NewsAdAdditional index={additionalAds} />
                      )}
                    </div>
                  )}
                </React.Fragment>
              );
            })}
            {!disableAds && <NewsAdLower />}
          </div>
        </div>
      </div>
    </NewsRequiredContainer>
  );
};

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext,
): Promise<GetServerSidePropsResult<NewsProps>> => {
  const { query } = context;

  const countryCode = getCountryCodeHeader(context.req.headers);

  const { pages } = await getPages(
    countryCode,
    { slug: "cannabis-glossary" },
    "1",
    "1",
  );
  const page = pages?.[0];

  if (!page) {
    return {
      notFound: true,
    };
  }

  const currentLetter = (query?.letter as string) || "a";

  const terms = await getTerms(countryCode, {
    term_letter: currentLetter,
  });

  const menuItems = terms.map((term) => ({
    slug: term.slug,
    title: term.title,
    url: term.link,
  }));

  return {
    props: {
      currentLetter,
      menuItems,
      page,
      terms,
    },
  };
};

export default CannabisGlossaryPage;
